import type { BuyLevelsMode } from "@features/investments/api";

export type SeriesMode = "annual" | "quarterly";
export type DetailTab = "summary" | "fundamentals" | "valuation" | "risk" | "history";
export type ETFDetailTab = "overview" | "composition" | "performance";
export type BuyLevelsHorizon = "3Y" | "5Y";
export type BuyLevelsScenario = "BEAR" | "BASE" | "BULL";
export type EquityDetailExportSection = "overview" | "fundamentals" | "entry" | "risk" | "full-memo";
export type EquityDetailExportFormat = "plain" | "markdown";

export type EquityDetailState = {
  mode: SeriesMode;
  activeTab: DetailTab;
  etfTab: ETFDetailTab;
  buyMode: BuyLevelsMode;
  buyHorizon: BuyLevelsHorizon;
  buyReverseScenario: BuyLevelsScenario;
  exportDrawerOpen: boolean;
  exportSection: EquityDetailExportSection;
  exportFormat: EquityDetailExportFormat;
  exportStatus: string;
  refreshMessage: string;
  refreshError: string;
};

export type EquityDetailAction =
  | { type: "tickerChanged"; }
  | { type: "buyLevelsChanged"; defaultMode?: BuyLevelsMode | null; }
  | { type: "setMode"; value: SeriesMode; }
  | { type: "setActiveTab"; value: DetailTab; }
  | { type: "setETFTab"; value: ETFDetailTab; }
  | { type: "setBuyMode"; value: BuyLevelsMode; }
  | { type: "setBuyHorizon"; value: BuyLevelsHorizon; }
  | { type: "setBuyReverseScenario"; value: BuyLevelsScenario; }
  | { type: "openExportDrawer"; section?: EquityDetailExportSection; }
  | { type: "closeExportDrawer"; }
  | { type: "setExportSection"; value: EquityDetailExportSection; }
  | { type: "setExportFormat"; value: EquityDetailExportFormat; }
  | { type: "setExportStatus"; value: string; }
  | { type: "clearRefreshFeedback"; }
  | { type: "setRefreshMessage"; value: string; }
  | { type: "setRefreshError"; value: string; };

export function createInitialEquityDetailState(): EquityDetailState {
  return {
    mode: "annual",
    activeTab: "summary",
    etfTab: "overview",
    buyMode: "STATIC",
    buyHorizon: "3Y",
    buyReverseScenario: "BASE",
    exportDrawerOpen: false,
    exportSection: "overview",
    exportFormat: "plain",
    exportStatus: "",
    refreshMessage: "",
    refreshError: "",
  };
}

export function equityDetailReducer(state: EquityDetailState, action: EquityDetailAction): EquityDetailState {
  switch (action.type) {
    case "tickerChanged":
      return {
        ...state,
        activeTab: "summary",
        etfTab: "overview",
        exportDrawerOpen: false,
        exportSection: "overview",
        exportFormat: "plain",
        exportStatus: "",
        refreshMessage: "",
        refreshError: "",
      };
    case "buyLevelsChanged":
      return {
        ...state,
        buyMode: action.defaultMode || "STATIC",
        buyHorizon: "3Y",
        buyReverseScenario: "BASE",
      };
    case "setMode":
      return { ...state, mode: action.value };
    case "setActiveTab":
      return { ...state, activeTab: action.value };
    case "setETFTab":
      return { ...state, etfTab: action.value };
    case "setBuyMode":
      return { ...state, buyMode: action.value };
    case "setBuyHorizon":
      return { ...state, buyHorizon: action.value };
    case "setBuyReverseScenario":
      return { ...state, buyReverseScenario: action.value };
    case "openExportDrawer":
      return {
        ...state,
        exportDrawerOpen: true,
        exportSection: action.section || state.exportSection,
      };
    case "closeExportDrawer":
      return {
        ...state,
        exportDrawerOpen: false,
        exportStatus: "",
      };
    case "setExportSection":
      return { ...state, exportSection: action.value };
    case "setExportFormat":
      return { ...state, exportFormat: action.value };
    case "setExportStatus":
      return { ...state, exportStatus: action.value };
    case "clearRefreshFeedback":
      return { ...state, refreshMessage: "", refreshError: "" };
    case "setRefreshMessage":
      return { ...state, refreshMessage: action.value, refreshError: "" };
    case "setRefreshError":
      return { ...state, refreshError: action.value, refreshMessage: "" };
    default:
      return state;
  }
}
